/**
 * NIGHTBOUND - the shelter (main game screen).
 *
 * Desktop:  stat panel | shelter | actions
 * Mobile:   identity + health, creature, needs, actions
 *
 * All state is the server's. The only client-side computation here is cosmetic:
 * projecting the need bars a fraction of a poll ahead, and ticking cooldowns.
 */

import { useMemo, useState } from 'react'
import { ACTION_COSTS, COOLDOWNS_SECONDS, NEED_THRESHOLDS, SLEEP } from '../../shared/constants'
import { SHELTER_GREETINGS } from '../../shared/reactions'
import { TRAINING_LIST } from '../../shared/training'
import type { CreatureView, InventoryEntryView } from '../../shared/types'
import { useGame } from '../state/GameProvider'
import { useCooldown, useDesktopLayout, useProjectedNeeds } from '../hooks'
import { CreatureRenderer } from '../components/CreatureRenderer'
import { ShelterScene } from '../components/ShelterScene'
import { ActionButton } from '../components/ActionButton'
import { ShelterLog } from '../components/ShelterLog'
import { ItemIcon } from '../components/ItemIcon'
import { Modal } from '../components/Modal'
import {
  AbilityPanel,
  ConditionStrip,
  DailyPanel,
  IdentityPanel,
  NeedsPanel,
  StatPanel,
  TrainingPanel,
} from '../components/panels'
import { Chip, Meter, Panel, PanelHeading } from '../components/ui'

export function ShelterScreen() {
  const { creature, inventory, log, clockOffsetMs, pending, act, feed, train, interact } = useGame()
  const desktop = useDesktopLayout()
  const projected = useProjectedNeeds(creature, clockOffsetMs)
  const [modal, setModal] = useState<'feed' | 'train' | null>(null)

  /** The cheapest training session - what the Train button must have on hand. */
  const trainEnergyCost = useMemo(
    () => Math.min(...TRAINING_LIST.map((discipline) => discipline.cost.energy)),
    [],
  )

  const feedCd = useCooldown(creature, 'FEED', clockOffsetMs)
  const cleanCd = useCooldown(creature, 'CLEAN', clockOffsetMs)
  const playCd = useCooldown(creature, 'PLAY', clockOffsetMs)
  const trainCd = useCooldown(creature, 'TRAIN', clockOffsetMs)

  const foodItems = useMemo(
    () => inventory.filter((entry) => entry.item.category === 'FOOD' && entry.quantity > 0),
    [inventory],
  )

  const greeting = useMemo(() => {
    if (!creature) return ''
    const lines = SHELTER_GREETINGS[creature.visual.mood]
    return lines[creature.daysTogether % lines.length] ?? lines[0] ?? ''
  }, [creature])

  if (!creature || !projected) return null

  const sleeping = creature.sleeping
  const canSleep = !sleeping && creature.needs.energy < SLEEP.maxEnergyToSleep
  const canPlay = !sleeping && creature.needs.energy >= ACTION_COSTS.PLAY.energy
  const canClean = !sleeping && creature.needs.hygiene < 99.5
  const canFeed = !sleeping && foodItems.length > 0
  const trainingCapReached = creature.daily.trainingSessions >= creature.daily.trainingSessionBudget
  const canTrain = !sleeping && !trainingCapReached && creature.needs.energy >= trainEnergyCost
  const busy = pending !== null

  const stage = (
    <ShelterScene state={creature.visual.state} className="h-full w-full">
      <CreatureRenderer
        species={creature.species}
        state={creature.visual.state}
        onInteract={() => void interact()}
        disabled={busy}
        ariaLabel={`Reach out to ${creature.name}`}
      />
    </ShelterScene>
  )

  const actions = (
    <>
      <div className="rounded-[4px] border border-cold-500/40 bg-night-950/50 p-3">
        <Meter
          label="Energy"
          icon="⚡"
          value={projected.energy}
          color="#8f9bb3"
          lowAt={NEED_THRESHOLDS.energy}
          compact
        />
        <p className="mt-1.5 text-[10px] leading-snug text-ash-400">
          Spent by <span className="text-cold-400">Play (−{ACTION_COSTS.PLAY.energy})</span> and{' '}
          <span className="text-blood-400">Train (−{trainEnergyCost})</span>. Rest to recover.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <ActionButton
          glyph="🍖"
          label="Feed"
          hint={canFeed ? `${foodItems.reduce((sum, f) => sum + f.quantity, 0)} food` : 'No food'}
          tone="ember"
          disabled={!canFeed || busy}
          cooldownSeconds={feedCd}
          cooldownTotal={COOLDOWNS_SECONDS.FEED}
          onClick={() => setModal('feed')}
        />
        <ActionButton
          glyph="≈"
          label="Clean"
          hint={canClean ? 'Free' : 'Spotless'}
          tone="rot"
          disabled={!canClean || busy}
          cooldownSeconds={cleanCd}
          cooldownTotal={COOLDOWNS_SECONDS.CLEAN}
          onClick={() => void act('clean')}
        />
        <ActionButton
          glyph="◇"
          label="Play"
          hint={`-${ACTION_COSTS.PLAY.energy} energy`}
          tone="cold"
          disabled={!canPlay || busy}
          cooldownSeconds={playCd}
          cooldownTotal={COOLDOWNS_SECONDS.PLAY}
          onClick={() => void act('play')}
        />
        <ActionButton
          glyph="⚒"
          label="Train"
          hint={trainingCapReached ? 'Rest today' : `-${trainEnergyCost} energy`}
          tone="blood"
          disabled={!canTrain || busy}
          cooldownSeconds={trainCd}
          cooldownTotal={COOLDOWNS_SECONDS.TRAIN}
          onClick={() => setModal('train')}
        />
      </div>
      <SleepControl
        sleeping={sleeping}
        canSleep={canSleep}
        busy={busy}
        sleepHours={creature.sleepHours}
        inSleepWindow={creature.inSleepWindow}
        onSleep={() => void act('sleep')}
        onWake={() => void act('wake')}
      />
    </>
  )

  // -------------------------------------------------------------------------
  // Desktop
  // -------------------------------------------------------------------------
  if (desktop) {
    return (
      <>
        <div className="mx-auto grid max-w-[1800px] grid-cols-[20rem_minmax(0,1fr)_21rem] gap-5 px-5 py-5">
          <aside className="space-y-4">
            <Panel className="p-4">
              <IdentityPanel creature={creature} />
              <div className="mt-3">
                <ConditionStrip creature={creature} />
              </div>
            </Panel>
            <NeedsPanel
              needs={projected}
              hp={creature.stats.hp}
              maxHp={creature.stats.maxHp}
            />
            <StatPanel creature={creature} />
            <TrainingPanel creature={creature} />
            <DailyPanel creature={creature} />
          </aside>

          <section className="space-y-4">
            <div className="h-[calc(100svh-2.5rem)] min-h-[520px] overflow-hidden rounded-[4px] border border-ash-700/40">
              {stage}
            </div>
            <Panel inset className="p-3">
              <p className="font-display text-sm italic leading-relaxed text-bone-300/90">
                {greeting}
              </p>
            </Panel>
          </section>

          <aside className="space-y-4">
            <Panel className="overflow-hidden">
              <PanelHeading
                right={<span className="nb-label text-[8px]">{creature.name}</span>}
              >
                Actions
              </PanelHeading>
              <div className="space-y-2 p-3">{actions}</div>
            </Panel>
            <ShelterLog entries={log} />
            <AbilityPanel creature={creature} />
          </aside>
        </div>
        <ActionModals
          modal={modal}
          onClose={() => setModal(null)}
          foodItems={foodItems}
          creature={creature}
          busy={busy}
          onFeed={async (key) => {
            const ok = await feed(key)
            if (ok) setModal(null)
          }}
          onTrain={async (discipline) => {
            const ok = await train(discipline)
            if (ok) setModal(null)
          }}
        />
      </>
    )
  }

  // -------------------------------------------------------------------------
  // Mobile
  // -------------------------------------------------------------------------
  return (
    <>
      <div className="mx-auto flex max-w-2xl flex-col gap-4 px-3 pb-28 pt-3">
        <Panel className="p-3">
          <IdentityPanel
            creature={creature}
            right={<Chip tone="neutral">Lv {creature.level}</Chip>}
          />
          <div className="mt-3">
            <ConditionStrip creature={creature} />
          </div>
        </Panel>

        <div className="aspect-[4/3] overflow-hidden rounded-[4px] border border-ash-700/40">
          {stage}
        </div>

        <NeedsPanel needs={projected} hp={creature.stats.hp} maxHp={creature.stats.maxHp} />

        <Panel className="overflow-hidden">
          <PanelHeading>Actions</PanelHeading>
          <div className="space-y-2 p-3">{actions}</div>
        </Panel>

        <Panel inset className="p-3">
          <p className="font-display text-sm italic leading-relaxed text-bone-300/90">{greeting}</p>
        </Panel>

        <ShelterLog entries={log} />
        <StatPanel creature={creature} />
        <TrainingPanel creature={creature} />
        <DailyPanel creature={creature} />
        <AbilityPanel creature={creature} />
      </div>

      <ActionModals
        modal={modal}
        onClose={() => setModal(null)}
        foodItems={foodItems}
        creature={creature}
        busy={busy}
        onFeed={async (key) => {
          const ok = await feed(key)
          if (ok) setModal(null)
        }}
        onTrain={async (discipline) => {
          const ok = await train(discipline)
          if (ok) setModal(null)
        }}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Sleep / wake control
// ---------------------------------------------------------------------------

function SleepControl({
  sleeping,
  canSleep,
  busy,
  sleepHours,
  inSleepWindow,
  onSleep,
  onWake,
}: {
  sleeping: boolean
  canSleep: boolean
  busy: boolean
  sleepHours: number
  inSleepWindow: boolean
  onSleep: () => void
  onWake: () => void
}) {
  return (
    <div className="space-y-1.5">
      {sleeping ? (
        <button
          type="button"
          disabled={busy}
          onClick={onWake}
          className="flex w-full items-center justify-between gap-3 rounded-[4px] border border-cold-500/50 bg-night-900/60 px-3 py-2.5 text-left transition-colors hover:bg-cold-500/10 disabled:opacity-55"
        >
          <span className="flex items-center gap-2">
            <span aria-hidden className="text-lg text-cold-400">
              ☀
            </span>
            <span className="nb-label text-[10px] text-bone-300">Wake it</span>
          </span>
          <span className="nb-numeric text-[10px] text-ash-400">asleep {sleepHours}h</span>
        </button>
      ) : (
        <button
          type="button"
          disabled={!canSleep || busy}
          onClick={onSleep}
          aria-label={canSleep ? 'Put it to sleep' : 'Not tired enough to sleep yet'}
          className="flex w-full items-center justify-between gap-3 rounded-[4px] border border-cold-500/50 bg-night-900/60 px-3 py-2.5 text-left transition-colors hover:bg-cold-500/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex items-center gap-2">
            <span aria-hidden className="text-lg text-cold-400">
              ☾
            </span>
            <span className="nb-label text-[10px] text-bone-300">Sleep</span>
          </span>
          <span className="nb-numeric text-[10px] text-ash-400">
            {inSleepWindow ? 'good hour' : canSleep ? 'ready' : 'not tired'}
          </span>
        </button>
      )}
      <p className="nb-label text-[8px] text-ash-500">
        {inSleepWindow
          ? 'Its natural sleep window is now - rest recovers faster.'
          : 'Sleeping outside its natural window recovers less.'}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Feed / train modals
// ---------------------------------------------------------------------------

function ActionModals({
  modal,
  onClose,
  foodItems,
  creature,
  busy,
  onFeed,
  onTrain,
}: {
  modal: 'feed' | 'train' | null
  onClose: () => void
  foodItems: InventoryEntryView[]
  creature: CreatureView
  busy: boolean
  onFeed: (key: string) => Promise<void>
  onTrain: (discipline: string) => Promise<void>
}) {
  const progressByDiscipline = useMemo(() => {
    const map = new Map(creature.training.map((row) => [row.discipline, row]))
    return map
  }, [creature.training])

  return (
    <>
      <Modal
        open={modal === 'feed'}
        onClose={onClose}
        title="Feed it"
        description={`It is fed to ${Math.round(creature.needs.hunger)}%. Feeding raises fullness and a little bond.`}
      >
        {foodItems.length === 0 ? (
          <p className="text-sm text-ash-400">There is nothing left to feed it.</p>
        ) : (
          <ul className="space-y-2">
            {foodItems.map((entry) => {
              const effects = entry.item.effects ?? {}
              return (
                <li key={entry.item.key}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void onFeed(entry.item.key)}
                    className="flex w-full items-center gap-3 rounded-[4px] border border-ash-700/50 bg-night-900/60 p-3 text-left transition-colors hover:border-ember-600/60 hover:bg-night-800/70 disabled:opacity-50"
                  >
                    <ItemIcon itemKey={entry.item.key} glyph={entry.item.glyph} size="md" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-sm text-bone-200">{entry.item.name}</span>
                        <span className="nb-numeric text-xs text-ash-400">×{entry.quantity}</span>
                      </span>
                      <span className="mt-0.5 block text-[11px] leading-snug text-ash-400">
                        {entry.item.description}
                      </span>
                      <span className="mt-1 flex flex-wrap gap-1">
                        {effects.hunger ? <Chip tone="warn">+{effects.hunger} fed</Chip> : null}
                        {effects.hp ? <Chip tone="good">+{effects.hp} health</Chip> : null}
                        {effects.bond ? <Chip tone="neutral">+{effects.bond} bond</Chip> : null}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </Modal>

      <Modal
        open={modal === 'train'}
        onClose={onClose}
        title="Train it"
        description={`Sessions today: ${creature.daily.trainingSessions}/${creature.daily.trainingSessionBudget}. Training drains energy, hunger and cleanliness.`}
      >
        <ul className="space-y-2">
          {TRAINING_LIST.map((discipline) => {
            const progress = progressByDiscipline.get(discipline.id)
            const affordable = creature.needs.energy >= discipline.cost.energy
            const capReached = creature.daily.trainingSessions >= creature.daily.trainingSessionBudget
            const disabled = busy || !affordable || capReached
            return (
              <li key={discipline.id}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => void onTrain(discipline.id)}
                  className="flex w-full items-start gap-3 rounded-[4px] border border-ash-700/50 bg-night-900/60 p-3 text-left transition-colors hover:border-ember-600/60 hover:bg-night-800/70 disabled:opacity-50"
                >
                  <span
                    aria-hidden
                    className="mt-0.5 text-2xl"
                    style={{ color: discipline.accent }}
                  >
                    {discipline.glyph}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-sm text-bone-200">{discipline.name}</span>
                      <span className="nb-numeric text-xs text-ash-400">
                        Lv {progress?.level ?? 1} · {Math.round((progress?.progress ?? 0) * 100)}%
                      </span>
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-ash-400">
                      {discipline.description}
                    </span>
                    <span className="mt-1 flex flex-wrap gap-1">
                      <Chip tone={affordable ? 'neutral' : 'bad'}>
                        -{discipline.cost.energy} energy
                      </Chip>
                      <Chip tone="neutral">raises {discipline.statLabel}</Chip>
                    </span>
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </Modal>
    </>
  )
}
